const cellClasses = (
  props: Record<string, any>,
  context: Record<string, any>,
  parent: Record<string, any>,
  state: Record<string, any>,
) => [
  'text-primary-900',
  // Font
  'leading-[normal]',
  // Position
  'relative',
  { 'sticky box-border border-b': parent.instance.frozenRow },
  {
    'sticky box-border border-b z-20 bg-white': props.frozen || props.frozen === '',
  },
  // Alignment
  'text-left',
  // Positionering
  'group-not-first/data-table-row:after:absolute',
  'group-not-first/data-table-row:after:top-0',
  'group-not-first/data-table-row:after:left-0',
  'group-not-first/data-table-row:after:right-0',
  'group-not-first/data-table-row:after:h-full',
  // Eerste cel heeft extra inspring
  'group-not-first/data-table-row:first:after:left-6',
  // Border
  'group-not-first/data-table-row:after:border-t',
  'group-not-first/data-table-row:after:border-t-grey-200',
  // Misc
  'group-not-first/data-table-row:after:pointer-events-none',
  // Reorder
  'group-data-[p-datatable-dragpoint-bottom="true"]/data-table-row:after:border-b-2',
  {
    'first:border-l border-r border-b': context == null ? undefined : context.showGridlines,
  },
  // Spacing
  {
    'py-1.5 px-2': (context == null ? undefined : context.size) === 'small' && !state.d_editing,
  },
  {
    'py-3.75 px-5': (context == null ? undefined : context.size) === 'large' && !state.d_editing,
  },
  {
    'h-14 px-3 pb-2.5 first:pl-6 nth-last-2:pr-6 last:pr-6':
      (context == null ? undefined : context.size) !== 'large' &&
      (context == null ? undefined : context.size) !== 'small' &&
      !state.d_editing,
  },
  { 'py-[0.6rem] px-2': state.d_editing },
  // Links
  '[&>div>a]:text-inherit [&>div>a]:grow [&>div>a]:underline [&>div>a]:hover:text-primary [&>div>a]:after:absolute [&>div>a]:after:inset-0',
];

export const datatable = {
  root: ({ props }: Record<string, any>) => ({
    class: [
      'relative group/data-table',
      // Flex & Alignment
      { 'flex flex-col gap-6': props.scrollable && props.scrollHeight === 'flex' },
      // Size
      { 'max-h-full': props.scrollable && props.scrollHeight === 'flex' },
    ],
  }),
  mask: {
    class: [
      'absolute',
      'top-0 left-0',
      'z-20',
      'flex items-center justify-center',
      'w-full h-full',
      'bg-surface-100/40',
      'transition duration-200',
    ],
  },
  loadingIcon: {
    class: 'w-8 h-8 animate-spin',
  },
  header: ({ props }: Record<string, any>) => ({
    class: [
      'group-not-first/data-table:pt-6 group-not-first/data-table:border-t group-not-first/data-table:border-t-grey-200',
      // Shape
      props.showGridlines ? 'border-x border-t border-b-0' : 'border-x-0',
    ],
  }),
  tableContainer: ({ props, attrs }: Record<string, any>) => ({
    class: [
      'rounded scrollbar-thin bg-white',
      {
        'border border-grey-200': !attrs.borderless,
        'border-none': attrs.borderless,
      },
      {
        relative: props.scrollable,
        'flex flex-col grow': props.scrollable && props.scrollHeight === 'flex',
      },
      // Size
      { 'h-full': props.scrollable && props.scrollHeight === 'flex' },
    ],
  }),
  table: {
    class: 'w-full border-spacing-0 border-separate',
  },
  footer: {
    class: ['font-bold', 'border-t-0 border-b border-x-0', 'p-4'],
  },
  thead: ({ context, parent }: Record<string, any>) => ({
    class: [
      {
        'top-0 z-40 sticky': context.scrollable,
        'bg-grey-50': !parent.attrs.borderless,
        'bg-white': parent.attrs.borderless,
        hidden: parent.attrs.headerless,
      },
    ],
  }),
  tbody: ({ instance, context }: Record<string, any>) => ({
    class: [
      'bg-white',
      {
        'sticky z-20': instance.frozenRow && context.scrollable,
      },
    ],
  }),
  tfoot: ({ context }: Record<string, any>) => ({
    class: [
      {
        'bottom-0 z-0': context.scrollable,
      },
    ],
  }),
  column: {
    reorderableRowHandle: {
      class: 'cursor-move size-4',
    },
    headercell: ({ context, props }: Record<string, any>) => ({
      class: [
        // Alignment
        {
          'text-left': props.align === 'left',
          'text-center justify-center': props.align === 'center',
          'text-right justify-end': props.align === 'right',
        },
        'font-semibold',
        'leading-[normal]',
        'outline-none',
        // Position
        { 'sticky z-20 border-b bg-grey-50': props.frozen || props.frozen === '' },
        { relative: context.resizable },

        // Shape
        {
          'first:border-l border-y border-r': context == null ? undefined : context.showGridlines,
        },
        'border-0 border-b border-solid',
        // Spacing
        (context == null ? undefined : context.size) === 'small'
          ? 'py-1.5 px-2'
          : (context == null ? undefined : context.size) === 'large'
            ? 'py-3.75 px-5'
            : 'py-[.8125rem] px-3 first:pl-6 nth-last-2:pr-6 last:pr-6',
        // Color
        'border-grey-200',
        // Transition
        { 'transition duration-200': props.sortable === '' || props.sortable },
        // Misc
        { 'cursor-pointer': props.sortable === '' || props.sortable },
        {
          'overflow-hidden border-y bg-clip-padding': context.resizable,
          // Resizable
        },
      ],
    }),
    columnHeaderContent: {
      class: 'text-nowrap',
    },
    columnTitle: {
      class: 'text-sm font-bold text-grey-500',
    },
    sort: {
      class: 'text-grey-600 leading-none',
    },
    bodyCell: ({ props, context, state, parent }: Record<string, any>) => ({
      class: [...cellClasses(props, context, parent, state), 'pt-2.5'],
    }),
    footerCell: ({ context }: Record<string, any>) => ({
      class: [
        // Font
        'font-bold',
        // Alignment
        'text-left',
        // Shape
        'border-0 border-b border-solid',
        { 'border-x border-y': context == null ? undefined : context.showGridlines },
        // Spacing
        (context == null ? undefined : context.size) === 'small'
          ? 'p-2'
          : (context == null ? undefined : context.size) === 'large'
            ? 'p-5'
            : 'p-4',
      ],
    }),
    sortIcon: {
      class: 'ml-1',
    },
    pcSortBadge: {
      class: [
        'flex items-center justify-center align-middle',
        'rounded-full',
        'w-[1.143rem] leading-[1.143rem]',
        'ml-2',
      ],
    },
    rowCheckbox: {
      root: {
        class: [
          'relative',
          'inline-flex',
          'align-bottom',
          'w-5',
          'h-5',
          'cursor-pointer',
          'select-none',
        ],
      },
      box: ({ props, context }: Record<string, any>) => ({
        class: [
          // Alignment
          'flex',
          'items-center',
          'justify-center',
          // Size
          'w-5',
          'h-5',
          // Shape
          'rounded',
          'border',
          // Colors
          {
            'border-primary bg-primary': context.checked,
          },
          // Invalid State
          'invalid:focus:ring-red-200',
          'invalid:hover:border-red-500',
          { 'border-red-500': props.invalid },
          // States
          {
            'peer-focus-visible:z-10 peer-focus-visible:outline-none peer-focus-visible:outline-offset-0 peer-focus-visible:ring-1 peer-focus-visible:ring-primary-500':
              !props.disabled,
            'select-none pointer-events-none cursor-default': props.disabled,
          },
          // Transitions
          'transition-colors',
          'duration-200',
        ],
      }),
      input: {
        class: [
          'peer',
          'w-full ',
          'h-full',
          'absolute',
          'top-0 left-0',
          'z-10',
          'p-0',
          'm-0',
          'opacity-0',
          'rounded',
          'outline-none',
          'border',
          'appearance-none',
          'cursor-pointer',
        ],
      },
      icon: {
        class: ['w-3.5', 'h-3.5', 'text-white', 'transition-all', 'duration-200'],
      },
    },
    transition: {
      class: 'p-3 flex flex-col gap-2',
      enterFromClass: 'opacity-0 scale-y-[0.8]',
      enterActiveClass:
        'transition-[transform,translate,scale,rotate,opacity] duration-120 ease-out',
      leaveActiveClass: 'transition-opacity duration-100 ease-linear',
      leaveToClass: 'opacity-0',
    },
  },
  bodyRow: ({ context, parent, props }: Record<string, any>) => ({
    class: [
      'group/data-table-row',
      {
        'hover:bg-grey-50 cursor-pointer': parent.attrs.showRowArrow || parent.attrs.rowLink,
      },
      // Color
      { 'font-bold z-20': props.frozenRow },
      // State
      {
        'focus:outline-none focus:outline-offset-0 focus:ring-1 focus:ring-primary-500 ring-inset':
          context.selectable,
      },
      // Transition
      {
        'transition duration-200': (props.selectionMode && !context.selected) || props.rowHover,
      },
      // Misc
      { 'cursor-pointer': props.selectionMode },
    ],
  }),
  rowGroupFooter: {
    class: ['sticky bottom-0 bg-white'],
  },
  rowGroupFooterCell: ({ context, parent, props, state }: Record<string, any>) => ({
    class: cellClasses(props, context, parent, state),
  }),
  emptyMessageCell: {
    class: 'p-0',
  },
};
